import { useCallback, useEffect, useState } from 'react'
import {
  api,
  ApiError,
  type ImportAttributeMatch,
  type ImportCategoryMatch,
  type ImportDecision,
  type ImportPreview,
  type ImportRun,
  type ImportSource,
  type StockImportPreview,
} from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, Field, FileInput, Input, Notice, Select } from '../ui/controls'

/**
 * Импорт товаров (п. 4.17).
 *
 * Три шага, и каждый отвечает на свой вопрос. «Откуда» — источник и ссылка.
 * «Что получилось» — предпросмотр: сколько создастся, сколько обновится,
 * что не разобрано (А-1605). «Куда положить» — сопоставление разделов
 * и характеристик источника с каталогом.
 *
 * Третий шаг существует потому, что второй поставщик называет то же самое
 * иначе. Программа предлагает соответствие и показывает меру уверенности,
 * но принимает решение оператор: «Размер» и «Размер производителя» похожи
 * до неразличимости и при этом разные.
 */
export default function ImportsPage() {
  const { state } = useAuth()
  const canWrite =
    state.status === 'authenticated' && state.me.permissions.write.import_goods

  const [sources, setSources] = useState<ImportSource[]>([])
  const [source, setSource] = useState('kaspi')
  const [ref, setRef] = useState('')
  const [file, setFile] = useState<File | null>(null)

  // Остатки идут отдельной операцией: файл называет товар артикулом
  // и не описывает его, зато требует явного режима применения (А-1608).
  const [stockFile, setStockFile] = useState<File | null>(null)
  const [stockMode, setStockMode] = useState<'set' | 'add'>('set')
  const [stockPreview, setStockPreview] = useState<
    { id: number; preview: StockImportPreview } | null
  >(null)
  const [runs, setRuns] = useState<ImportRun[]>([])
  const [current, setCurrent] = useState<ImportRun | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [pending, setPending] = useState(false)

  const loadRuns = useCallback(async () => {
    const list = await api.imports()
    setRuns(list)
    // Обход идёт минутами и переживает перезагрузку страницы. Без этого
    // оператор, обновивший вкладку, видел бы в истории «выполняется»
    // и ничего больше: панель хода работы привязана к текущей операции,
    // а её после перезагрузки не было.
    setCurrent((now) => {
      if (now && now.state !== 'done' && now.state !== 'cancelled') return now
      return list.find((r) => r.state === 'running' || r.state === 'pending') ?? now
    })
    return list
  }, [])

  useEffect(() => {
    void api.importSources().then(setSources)
    void loadRuns()
  }, [loadRuns])

  // Пока операция выполняется, её состояние опрашивается: обход чужой
  // витрины длится минуты, и оператор должен видеть движение (А-1611).
  useEffect(() => {
    if (!current || (current.state !== 'running' && current.state !== 'pending')) return
    const timer = setInterval(async () => {
      try {
        const run = await api.importRun(current.id)
        setCurrent(run)
        // Строка в истории показывает тот же ход работы, поэтому список
        // обновляется вместе с ней, а не только по завершении.
        setRuns((list) => list.map((r) => (r.id === run.id ? run : r)))
        if (run.state !== 'running' && run.state !== 'pending') {
          void loadRuns()
          if (run.state === 'preview') void loadPreview(run.id)
          if (run.state === 'failed') {
            setNotice({ kind: 'error', text: run.progress_note || 'Импорт не удался' })
          }
          if (run.state === 'done') {
            setNotice({
              kind: 'success',
              text: `Готово: создано ${run.created_rows}, обновлено ${run.updated_rows}`
                + (run.failed_rows ? `, не удалось ${run.failed_rows}` : ''),
            })
            setPreview(null)
          }
        }
      } catch {
        /* сеть подводит — следующий опрос повторит */
      }
    }, 2000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.state])

  async function loadPreview(id: number) {
    try {
      setPreview(await api.importPreview(id))
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Предпросмотр недоступен' })
    }
  }

  async function start() {
    setPending(true)
    setNotice(null)
    setPreview(null)
    try {
      const run =
        source === 'file' && file
          ? await api.uploadImport(file)
          : await api.startImport(source, ref.trim())
      setCurrent(run)
      await loadRuns()
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось начать' })
    } finally {
      setPending(false)
    }
  }

  async function apply() {
    if (!current || !preview) return
    setPending(true)
    setNotice(null)
    try {
      await api.applyImport(current.id, preview.categories, preview.attributes)
      setCurrent({ ...current, state: 'running', progress: 0, progress_note: '' })
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось применить' })
    } finally {
      setPending(false)
    }
  }

  const busy = current?.state === 'running' || current?.state === 'pending'

  return (
    <section className="max-w-6xl">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Импорт товаров</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          Товары, характеристики и фотографии переносятся из внешнего источника.
          Ключ — артикул: существующие карточки обновляются, новые создаются.
        </p>
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {/* Шаг первый: откуда брать */}
      <Block title="Источник">
        <div className="field-row grid sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)_auto]">
          <Field label="Откуда">
            <Select value={source} onChange={(e) => setSource(e.target.value)}
              disabled={!canWrite || busy}
            >
              {sources.map((s) => (
                <option key={s.code} value={s.code} disabled={!s.ready}>
                  {s.name}
                  {s.ready ? '' : ' — скоро'}
                </option>
              ))}
            </Select>
          </Field>
          {source === 'file' ? (
            <Field label="Файл выгрузки" hint={sources.find((s) => s.code === source)?.hint}>
              <FileInput
                accept=".xml,.yml,text/xml,application/xml"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={!canWrite || busy}
              />
            </Field>
          ) : (
            <Field
              label="Адрес магазина"
              hint={sources.find((s) => s.code === source)?.hint}
            >
              <Input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="https://kaspi.kz/shop/m/9759027/products"
                disabled={!canWrite || busy}
              />
            </Field>
          )}
          <div className="field-action"><Button className="field-action"
            variant="primary"
            onClick={start}
            disabled={
              !canWrite || busy || pending ||
              (source === 'file' ? file === null : ref.trim() === '')
            }
          >
            {busy ? 'Идёт разбор…' : source === 'file' ? 'Загрузить файл' : 'Забрать товары'}
          </Button></div>
        </div>

        {busy && current && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-ink-600 dark:text-ink-400">
                {current.progress_note || 'подготовка'}
              </span>
              <span className="font-mono tabular-nums text-ink-500">
                {current.progress > 0 ? `${current.progress}%` : '…'}
                {remaining(current) && (
                  <span className="ml-3 text-ink-400">≈ {remaining(current)}</span>
                )}
              </span>
            </div>
            {/* Пока доля неизвестна — на этапе сбора перечня общее число
                товаров ещё не выяснено, — полоса бежит вместо того, чтобы
                стоять на нуле: неподвижная полоса читается как поломка. */}
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-200 dark:bg-ink-800">
              {current.progress > 0 ? (
                <div
                  className="h-full bg-brand-500 transition-[width] duration-500"
                  style={{ width: `${current.progress}%` }}
                />
              ) : (
                <div className="h-full w-1/3 animate-[indeterminate_1.4s_ease-in-out_infinite] rounded-full bg-brand-500" />
              )}
            </div>
            <button
              type="button"
              onClick={() => void api.cancelImport(current.id).then(loadRuns)}
              className="mt-2 text-xs text-ink-500 hover:text-danger-700 dark:hover:text-danger-100"
            >
              Остановить
            </button>
          </div>
        )}
      </Block>

      {/* Шаг второй: что получилось */}
      {preview && current && (
        <>
          <Block title="Что произойдёт">
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <Figure label="Всего получено" value={preview.total} />
              <Figure label="Создать" value={preview.create} />
              <Figure label="Обновить" value={preview.update} />
              <Figure
                label="Ошибок"
                value={preview.failed}
                warn={preview.failed > 0}
                href={preview.failed > 0 ? `/api/admin/imports/${current.id}/errors.csv` : undefined}
              />
            </div>

            {preview.errors.length > 0 && (
              <ul className="mt-4 space-y-1 text-sm text-warning-700 dark:text-warning-100">
                {preview.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>
                    строка {e.row}
                    {e.sku ? ` · ${e.sku}` : ''} · {e.field}: {e.message}
                  </li>
                ))}
                {preview.errors.length > 5 && (
                  <li className="text-ink-500">…всего {preview.errors.length}</li>
                )}
              </ul>
            )}

            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[52rem] text-sm">
                <thead className="text-xs uppercase text-ink-500">
                  <tr className="border-b border-ink-200 dark:border-ink-800">
                    <th className="py-2 text-left font-medium">Действие</th>
                    <th className="py-2 text-left font-medium">Артикул</th>
                    <th className="py-2 text-left font-medium">Название</th>
                    <th className="py-2 text-right font-medium">Цена</th>
                    <th className="py-2 text-right font-medium">Остаток</th>
                    <th className="py-2 text-right font-medium">Фото</th>
                    <th className="py-2 text-right font-medium">Значений</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-b border-ink-100 dark:border-ink-900">
                      <td className="py-1.5">
                        <ActionTag action={r.action} />
                      </td>
                      <td className="py-1.5 font-mono text-xs">{r.sku}</td>
                      <td className="max-w-md truncate py-1.5">{r.title}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.price.toLocaleString('ru')}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.stock}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.images}</td>
                      <td className="py-1.5 text-right tabular-nums">{r.attributes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Block>

          {/* Шаг третий: куда положить */}
          <Block title="Разделы">
            <p className="mb-3 text-sm text-ink-500">
              Совпавшие дословно уже сопоставлены. Остальные будут созданы —
              либо укажите существующий раздел каталога.
            </p>
            <MatchTable
              rows={preview.categories.map((c) => ({
                key: c.source_path,
                name: c.source_path,
                count: c.products,
                note: '',
                decision: c.decision,
                target: c.category_id,
                suggested: c.suggested,
                saved: c.saved,
              }))}
              onChange={(key, decision, target) =>
                setPreview({
                  ...preview,
                  categories: preview.categories.map((c) =>
                    c.source_path === key ? { ...c, decision, category_id: target } : c,
                  ) as ImportCategoryMatch[],
                })
              }
            />
          </Block>

          <Block title="Характеристики">
            <p className="mb-3 text-sm text-ink-500">
              «Список» означает, что значения записаны у поставщика одной строкой
              через запятую и будут разделены — каждое станет отдельным фильтром.
            </p>
            <MatchTable
              rows={preview.attributes.map((a) => ({
                key: a.source_name,
                name: a.source_name,
                count: a.products,
                note: `${a.values} знач.${a.is_list ? ' · список' : ''}`,
                decision: a.decision,
                target: a.attribute_id,
                suggested: a.suggested,
                saved: a.saved,
              }))}
              onChange={(key, decision, target) =>
                setPreview({
                  ...preview,
                  attributes: preview.attributes.map((a) =>
                    a.source_name === key ? { ...a, decision, attribute_id: target } : a,
                  ) as ImportAttributeMatch[],
                })
              }
            />
          </Block>

          <div className="mb-8 flex items-center gap-3">
            <Button variant="primary" onClick={apply} disabled={!canWrite || pending || busy}>
              Применить к каталогу
            </Button>
            <span className="text-sm text-ink-500">
              Создастся {preview.create}, обновится {preview.update}.
            </span>
          </div>
        </>
      )}

      {/* Остатки (А-1608, А-1609) */}
      <Block title="Остатки отдельным файлом">
        <p className="mb-3 text-sm text-ink-500">
          Таблица со столбцами «Артикул», «Склад», «Количество». Остаток известен
          складу, а не поставщику, поэтому товарный импорт его не трогает.
        </p>
        <div className="field-row grid sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]">
          <Field label="Режим" hint={stockMode === 'set' ? 'привести к указанному' : 'прибавить к текущему'}>
            <Select value={stockMode} onChange={(e) => setStockMode(e.target.value as 'set' | 'add')}
              disabled={!canWrite}
            >
              <option value="set">Установить значение</option>
              <option value="add">Добавить к текущему</option>
            </Select>
          </Field>
          <Field label="Файл остатков">
            <FileInput
              accept=".csv,text/csv"
              onChange={(e) => setStockFile(e.target.files?.[0] ?? null)}
              disabled={!canWrite}
            />
          </Field>
          <div className="field-action"><Button className="field-action"
            onClick={async () => {
              if (!stockFile) return
              setPending(true)
              setNotice(null)
              try {
                const r = await api.uploadStock(stockFile, stockMode)
                setStockPreview({ id: r.import.id, preview: r.preview })
                await loadRuns()
              } catch (e) {
                setNotice({
                  kind: 'error',
                  text: e instanceof ApiError ? e.message : 'Файл не принят',
                })
              } finally {
                setPending(false)
              }
            }}
            disabled={!canWrite || pending || stockFile === null}
          >
            Проверить файл
          </Button></div>
        </div>

        {stockPreview && (
          <div className="mt-5">
            <div className="mb-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span>
                строк <b className="tabular-nums">{stockPreview.preview.total}</b>
              </span>
              <span>
                изменят остаток{' '}
                <b className="tabular-nums">{stockPreview.preview.changed}</b>
              </span>
              <span className="text-ink-500">
                без изменений {stockPreview.preview.noop}
              </span>
              {stockPreview.preview.failed > 0 && (
                <span className="text-warning-700 dark:text-warning-100">
                  ошибок {stockPreview.preview.failed}
                </span>
              )}
            </div>

            {stockPreview.preview.errors.length > 0 && (
              <ul className="mb-3 space-y-1 text-sm text-warning-700 dark:text-warning-100">
                {stockPreview.preview.errors.slice(0, 6).map((e, i) => (
                  <li key={i}>
                    строка {e.row}
                    {e.sku ? ` · ${e.sku}` : ''} · {e.field}: {e.message}
                  </li>
                ))}
              </ul>
            )}

            {stockPreview.preview.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[38rem] text-sm">
                  <thead className="text-xs uppercase text-ink-500">
                    <tr className="border-b border-ink-200 dark:border-ink-800">
                      <th className="py-2 pr-4 text-left font-medium">Артикул</th>
                      <th className="py-2 pr-4 text-left font-medium">Товар</th>
                      <th className="py-2 pr-4 text-left font-medium">Склад</th>
                      <th className="py-2 pr-6 text-right font-medium">Было</th>
                      <th className="py-2 pr-6 text-right font-medium">Станет</th>
                      <th className="py-2 text-right font-medium">Движение</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockPreview.preview.rows.slice(0, 20).map((r) => (
                      <tr key={r.row} className="border-b border-ink-100 dark:border-ink-900">
                        <td className="py-1.5 pr-4 font-mono text-xs">{r.sku}</td>
                        <td className="max-w-xs truncate py-1.5 pr-4">{r.title}</td>
                        <td className="py-1.5 pr-4 font-mono text-xs">{r.warehouse}</td>
                        <td className="py-1.5 pr-6 text-right tabular-nums text-ink-500">
                          {r.current}
                        </td>
                        <td className="py-1.5 pr-6 text-right tabular-nums">
                          {stockPreview.preview.mode === 'set' ? r.quantity : r.current + r.delta}
                        </td>
                        <td
                          className={`py-1.5 text-right tabular-nums ${
                            r.delta > 0
                              ? 'text-success-700 dark:text-success-100'
                              : r.delta < 0
                                ? 'text-danger-700 dark:text-danger-100'
                                : 'text-ink-400'
                          }`}
                        >
                          {r.delta > 0 ? `+${r.delta}` : r.delta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <Button
                variant="primary"
                onClick={async () => {
                  setPending(true)
                  setNotice(null)
                  try {
                    const r = await api.applyStock(stockPreview.id, stockPreview.preview.mode)
                    setStockPreview(null)
                    setStockFile(null)
                    await loadRuns()
                    setNotice({
                      kind: r.failed > 0 ? 'warning' : 'success',
                      text: `Движений записано: ${r.applied}` +
                        (r.failed > 0 ? `, строк с ошибками: ${r.failed}` : ''),
                    })
                  } catch (e) {
                    setNotice({
                      kind: 'error',
                      text: e instanceof ApiError ? e.message : 'Не удалось применить',
                    })
                  } finally {
                    setPending(false)
                  }
                }}
                disabled={!canWrite || pending || stockPreview.preview.changed === 0}
              >
                Применить остатки
              </Button>
              <button
                type="button"
                onClick={() => setStockPreview(null)}
                className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
              >
                Отменить
              </button>
            </div>
          </div>
        )}
      </Block>

      {/* История (А-1610) */}
      <Block title="История">
        {runs.length === 0 ? (
          <p className="text-sm text-ink-500">Импортов ещё не было.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-ink-500">
              <tr className="border-b border-ink-200 dark:border-ink-800">
                <th className="py-2 text-left font-medium">Когда</th>
                <th className="py-2 text-left font-medium">Источник</th>
                <th className="py-2 text-left font-medium">Состояние</th>
                <th className="py-2 text-right font-medium">Получено</th>
                <th className="py-2 text-right font-medium">Создано</th>
                <th className="py-2 text-right font-medium">Обновлено</th>
                <th className="py-2 text-right font-medium">Ошибок</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-ink-100 dark:border-ink-900">
                  <td className="py-1.5 whitespace-nowrap">{r.created_at.slice(0, 16).replace('T', ' ')}</td>
                  <td className="max-w-xs truncate py-1.5">{r.source_ref}</td>
                  <td className="py-1.5">
                    <StateTag state={r.state} />
                    {(r.state === 'running' || r.state === 'pending') && r.progress_note && (
                      <div className="text-2xs text-ink-500">
                        {r.progress_note}
                        {r.progress > 0 ? ` · ${r.progress}%` : ''}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{r.total_rows}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.created_rows}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.updated_rows}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.failed_rows}</td>
                  <td className="py-1.5 text-right">
                    {r.state === 'preview' && (
                      <button
                        type="button"
                        onClick={() => {
                          setCurrent(r)
                          void loadPreview(r.id)
                        }}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        Продолжить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>
    </section>
  )
}

// --------------------------------------------------------------------------

type MatchRow = {
  key: string
  name: string
  count: number
  note: string
  decision: ImportDecision
  target: number | null
  suggested: { id: number; name: string; score: number } | null
  saved: boolean
}

/**
 * Таблица решений. Показываются все записи источника: строка, по которой
 * решение уже принято, всё равно видна — иначе оператор не сможет его
 * изменить.
 */
function MatchTable({
  rows,
  onChange,
}: {
  rows: MatchRow[]
  onChange: (key: string, decision: ImportDecision, target: number | null) => void
}) {
  const [all, setAll] = useState(false)
  // Внимания требуют только те, по которым есть выбор: остальные показываются
  // по требованию, чтобы сотня строк не заслоняла десяток спорных.
  const notable = rows.filter((r) => r.decision !== 'map' || r.suggested === null)
  const shown = all ? rows : notable.length > 0 ? notable : rows.slice(0, 10)

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[44rem] text-sm">
          <thead className="text-xs uppercase text-ink-500">
            <tr className="border-b border-ink-200 dark:border-ink-800">
              <th className="py-2 pr-4 text-left font-medium">У поставщика</th>
              <th className="py-2 pr-6 text-right font-medium">Товаров</th>
              <th className="py-2 pr-4 text-left font-medium">Решение</th>
              <th className="py-2 text-left font-medium">В каталоге</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.key} className="border-b border-ink-100 dark:border-ink-900">
                <td className="py-1.5 pr-4">
                  <span className="text-ink-900 dark:text-ink-100">{r.name}</span>
                  {r.note && <span className="ml-2 text-xs text-ink-500">{r.note}</span>}
                </td>
                <td className="py-1.5 pr-6 text-right tabular-nums">{r.count}</td>
                <td className="py-1.5 pr-4">
                  <Select value={r.decision} onChange={(e) => {
                      const d = e.target.value as ImportDecision
                      onChange(r.key, d, d === 'map' ? r.suggested?.id ?? r.target : null)
                    }}
                    className="rounded-md border border-ink-300 bg-white px-2 py-1 text-xs text-ink-900 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
                  >
                    <option value="create">создать</option>
                    <option value="map" disabled={!r.suggested && r.target === null}>
                      сопоставить
                    </option>
                    <option value="skip">пропустить</option>
                  </Select>
                </td>
                <td className="py-1.5">
                  {r.decision === 'map' && r.suggested ? (
                    <span className="text-ink-700 dark:text-ink-300">
                      {r.suggested.name}
                      {r.suggested.score < 1 && (
                        <span className="ml-2 text-xs text-warning-700 dark:text-warning-100">
                          похоже на {Math.round(r.suggested.score * 100)}%
                        </span>
                      )}
                      {r.saved && <span className="ml-2 text-xs text-ink-400">решено ранее</span>}
                    </span>
                  ) : r.suggested ? (
                    <span className="text-xs text-ink-500">
                      похоже на «{r.suggested.name}» ({Math.round(r.suggested.score * 100)}%)
                    </span>
                  ) : (
                    <span className="text-xs text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown.length && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="mt-2 text-xs text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
        >
          Показать все {rows.length}
        </button>
      )}
    </>
  )
}

function Figure({
  label,
  value,
  warn,
  href,
}: {
  label: string
  value: number
  warn?: boolean
  href?: string
}) {
  return (
    <div>
      <div className="text-xs uppercase text-ink-500">{label}</div>
      <div
        className={`mt-0.5 text-2xl font-semibold tabular-nums ${
          warn ? 'text-warning-700 dark:text-warning-100' : 'text-ink-900 dark:text-ink-50'
        }`}
      >
        {href ? (
          <a href={href} className="hover:underline" download>
            {value.toLocaleString('ru')}
          </a>
        ) : (
          value.toLocaleString('ru')
        )}
      </div>
    </div>
  )
}

function ActionTag({ action }: { action: string }) {
  const map: Record<string, { text: string; cls: string }> = {
    create: { text: 'создать', cls: 'bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100' },
    update: { text: 'обновить', cls: 'bg-info-100 dark:bg-info-700/25 text-info-700 dark:text-info-100' },
    error: { text: 'ошибка', cls: 'bg-danger-100 dark:bg-danger-700/25 text-danger-700 dark:text-danger-100' },
  }
  const v = map[action] ?? { text: action, cls: '' }
  return <span className={`rounded-full px-2 py-0.5 text-xs ${v.cls}`}>{v.text}</span>
}

function StateTag({ state }: { state: ImportRun['state'] }) {
  const map: Record<string, { text: string; cls: string }> = {
    pending: { text: 'подготовка', cls: 'text-ink-500' },
    running: { text: 'выполняется', cls: 'text-info-700 dark:text-info-100' },
    preview: { text: 'ждёт решения', cls: 'text-warning-700 dark:text-warning-100' },
    done: { text: 'применён', cls: 'text-success-700 dark:text-success-100' },
    failed: { text: 'не удался', cls: 'text-danger-700 dark:text-danger-100' },
    cancelled: { text: 'остановлен', cls: 'text-ink-500' },
  }
  const v = map[state] ?? { text: state, cls: '' }
  return <span className={`text-xs ${v.cls}`}>{v.text}</span>
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6 rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950">
      <h3 className="mb-3 text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
      {children}
    </div>
  )
}

/**
 * Оценка оставшегося времени по уже пройденной доле.
 *
 * Считается от начала операции: если четверть сделана за две минуты,
 * на остальные три четверти уйдёт около шести. Оценка грубая и нарочно
 * округлена до минут — обход чужой витрины идёт неровно, и точность
 * до секунды была бы обманом.
 */
function remaining(run: ImportRun): string | null {
  if (run.progress < 5 || run.progress >= 100) return null
  const elapsed = (Date.now() - new Date(run.created_at).getTime()) / 1000
  if (elapsed < 10) return null
  const left = (elapsed / run.progress) * (100 - run.progress)
  if (left < 60) return 'меньше минуты'
  return `${Math.round(left / 60)} мин`
}
